/**
 * Picking and merging parts of the next-intl catalogue (`LN-UI03`).
 *
 * A layer of the site gives its client code only the message paths its code
 * reads (`lib/i18n-client-namespaces.generated.ts`), and a nested layer adds
 * its own to what the layers above it gave (`components/i18n/MessagesLayer`).
 */

export type MessageTree = { [key: string]: MessageNode };
type MessageNode = string | readonly MessageNode[] | MessageTree;

function isTree(node: unknown): node is MessageTree {
  return typeof node === "object" && node !== null && !Array.isArray(node);
}

/**
 * The subtrees and messages `paths` name, in the catalogue's own shape. A path
 * that runs into an array takes the whole array (arrays are read whole, with
 * `t.raw`); a path the catalogue does not have is skipped.
 */
export function pickMessages(all: MessageTree, paths: readonly string[]): MessageTree {
  const out: MessageTree = {};
  for (const path of paths) {
    const parts = path.split(".");
    let source: MessageNode = all;
    let depth = 0;
    while (depth < parts.length && isTree(source)) {
      const next: MessageNode | undefined = source[parts[depth]!];
      if (next === undefined) break;
      source = next;
      depth += 1;
    }
    const complete = depth === parts.length || (depth > 0 && Array.isArray(source));
    if (!complete || depth === 0) continue;
    let target = out;
    for (const part of parts.slice(0, depth - 1)) {
      const existing = target[part];
      if (!isTree(existing)) target[part] = {};
      target = target[part] as MessageTree;
    }
    const last = parts[depth - 1]!;
    target[last] = isTree(source) && isTree(target[last]) ? mergeMessages(target[last] as MessageTree, source) : source;
  }
  return out;
}

/** `extra` laid over `base`: subtrees merge, anything else from `extra` wins. */
export function mergeMessages(base: MessageTree, extra: MessageTree): MessageTree {
  const out: MessageTree = { ...base };
  for (const [key, value] of Object.entries(extra)) {
    const current = out[key];
    out[key] = isTree(current) && isTree(value) ? mergeMessages(current, value) : value;
  }
  return out;
}
